import { useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Loader2, Eye, EyeOff } from 'lucide-react';
import { useAdminRentalVehicles, useDeleteRentalVehicle, useUpdateRentalVehicle } from '@/hooks/useRentalVehicles';
import { VehicleFormDialog } from '@/components/admin/VehicleFormDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const AdminRentalVehicles = () => {
  const { data: vehicles, isLoading } = useAdminRentalVehicles();
  const deleteVehicle = useDeleteRentalVehicle();
  const updateVehicle = useUpdateRentalVehicle();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState<string | null>(null);

  const handleEdit = (vehicle: any) => {
    setSelectedVehicle(vehicle);
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setSelectedVehicle(null);
    setDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    setVehicleToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (vehicleToDelete) {
      deleteVehicle.mutate(vehicleToDelete);
      setDeleteDialogOpen(false);
      setVehicleToDelete(null);
    }
  };

  const toggleActive = (id: string, currentStatus: boolean) => {
    updateVehicle.mutate({ id, updates: { is_active: !currentStatus } });
  };

  const toggleAvailable = (id: string, currentStatus: boolean) => {
    updateVehicle.mutate({ id, updates: { is_available: !currentStatus } });
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Rental Vehicles</h1>
          <Button onClick={handleAdd}>
            <Plus className="w-4 h-4 mr-2" />
            Add Vehicle
          </Button>
        </div>

        <div className="bg-card rounded-lg shadow">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vehicle</TableHead>
                <TableHead>Brand/Model</TableHead>
                <TableHead>Year</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Daily Rate</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Available</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vehicles && vehicles.length > 0 ? (
                vehicles.map((vehicle) => (
                  <TableRow key={vehicle.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {vehicle.main_image_url && (
                          <img
                            src={vehicle.main_image_url}
                            alt={vehicle.vehicle_name}
                            className="w-16 h-12 object-cover rounded"
                          />
                        )}
                        <div>
                          <p className="font-medium">{vehicle.vehicle_name}</p>
                          {vehicle.is_featured && (
                            <Badge variant="secondary" className="mt-1">Featured</Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {vehicle.brand} {vehicle.model}
                    </TableCell>
                    <TableCell>{vehicle.year}</TableCell>
                    <TableCell>{vehicle.category_id || 'N/A'}</TableCell>
                    <TableCell>${vehicle.daily_rate}</TableCell>
                    <TableCell>
                      <Badge variant={vehicle.is_active ? 'default' : 'secondary'}>
                        {vehicle.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={vehicle.is_available ? 'default' : 'destructive'}>
                        {vehicle.is_available ? 'Available' : 'Rented'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleActive(vehicle.id, vehicle.is_active)}
                          title={vehicle.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {vehicle.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleAvailable(vehicle.id, vehicle.is_available)}
                          title={vehicle.is_available ? 'Mark as Rented' : 'Mark as Available'}
                        >
                          {vehicle.is_available ? '🚗' : '🔒'}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(vehicle)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(vehicle.id)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No vehicles found. Click "Add Vehicle" to create one.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <VehicleFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          vehicle={selectedVehicle}
        />

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the vehicle.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminRentalVehicles;